import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

export interface SubcontractorAssignment {
  id: string;
  jobId: string;
  parentContractorId: string;
  subContractorId: string;
  subContractorName: string;
  subContractorCompany: string | null;
  scope: string | null;
  status: "pending" | "active" | "complete" | "cancelled";
  assignedAt: string;
  assignedBy: string;
}

type AssignmentRow = {
  id: string;
  job_id: string;
  parent_contractor_id: string;
  sub_contractor_id: string;
  scope: string | null;
  status: string;
  assigned_at: string;
  assigned_by: string;
  contractors?: { name: string | null; company_name: string | null } | null;
};

function mapRow(row: AssignmentRow): SubcontractorAssignment {
  return {
    id: row.id,
    jobId: row.job_id,
    parentContractorId: row.parent_contractor_id,
    subContractorId: row.sub_contractor_id,
    subContractorName: row.contractors?.name ?? "Unknown",
    subContractorCompany: row.contractors?.company_name ?? null,
    scope: row.scope,
    status: row.status as SubcontractorAssignment["status"],
    assignedAt: row.assigned_at,
    assignedBy: row.assigned_by,
  };
}

export const SubcontractorAssignmentService = {
  async listByJob(jobId: string): Promise<SubcontractorAssignment[]> {
    const { data, error } = await supabase
      .from("job_subcontractor_assignments")
      .select("*, contractors(name, company_name)")
      .eq("job_id", jobId)
      .neq("status", "cancelled")
      .order("assigned_at", { ascending: true });

    if (error || !data) {
      console.warn("[SubcontractorAssignmentService.listByJob]", error);
      return [];
    }

    return (data as AssignmentRow[]).map(mapRow);
  },

  /**
   * Advance all pending assignments for a job to active.
   * Called when a job reaches "In Progress" stage.
   */
  async advanceToActive(jobId: string): Promise<void> {
    const orgId = await getCurrentOrganizationId();
    const { error } = await supabase
      .from("job_subcontractor_assignments")
      .update({ status: "active", updated_at: new Date().toISOString() })
      .eq("job_id", jobId)
      .eq("status", "pending")
      .eq("organization_id", orgId);

    if (error) {
      console.error("[SubcontractorAssignmentService.advanceToActive]", error);
    }
  },

  /**
   * Advance all active assignments for a job to complete.
   * Called on job closeout approval.
   */
  async advanceToComplete(jobId: string): Promise<void> {
    const orgId = await getCurrentOrganizationId();
    const { error } = await supabase
      .from("job_subcontractor_assignments")
      .update({ status: "complete", updated_at: new Date().toISOString() })
      .eq("job_id", jobId)
      .eq("status", "active")
      .eq("organization_id", orgId);

    if (error) {
      console.error("[SubcontractorAssignmentService.advanceToComplete]", error);
    }
  },
};
